import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, AsyncValidatorFn, FormBuilder, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { BaseComponent } from 'app/base/base.component';
import { Country } from 'app/core/models/country';
import { MerchantService } from 'app/core/services/merchant.service';
import { NotifMessageService } from 'app/core/services/notifmessage.service';
import { SupplierService } from 'app/core/services/supplier.service';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';

@Component({
  selector: 'app-edit-contact-info',
  templateUrl: './edit-contact-info.component.html'
})
export class EditContactInfoComponent extends BaseComponent implements OnInit {

  @Input() address: any;
  @Input() contact: any;
  @Input() isCreateMode?: boolean = false;

  countryItems: Array<Country> = Array<Country>();
  selectedCountryItem;

  modalResponseSubscription: Subscription;
  contactForm : FormGroup;

  @Output() contactFormIsValidEvent = new EventEmitter<boolean>();

  constructor(
    private formBuilder: FormBuilder,
    protected notifService: NotifMessageService,
    private supplierService: SupplierService,
    private merchantService: MerchantService,
    public router: Router,
    private translateService: TranslateService
  ) { super(router); }

  ngOnInit() : void {
    if (!this.isCreateMode) {
      this.address = {
        address_line1: this.address && this.address.address_line1 ? this.address.address_line1 : "",
        address_line2: this.address && this.address.address_line2 ? this.address.address_line2 : "",
        address_line3: this.address && this.address.address_line3 ? this.address.address_line3 : "",
        zip_code: this.address && this.address.zip_code ? this.address.zip_code : "",
        city: this.address && this.address.city ? this.address.city : "",
        country: this.address && this.address.country ? this.address.country : ""
      }

      this.contact = {
        firstName: this.contact && this.contact.firstName ? this.contact.firstName : "",
        lastName: this.contact && this.contact.lastName ? this.contact.lastName : "",
        tel: this.contact && this.contact.tel ? this.contact.tel : "",
        email: this.contact && this.contact.email ? this.contact.email : ""
      }
    }
    this.initForm();    
    this.countryItems = this.getCountrys();
    
    if (!this.isCreateMode) {
      let contactInfoInit = {
        address: {
          address_line1: this.address.address_line1,
          address_line2: this.address.address_line2,
          address_line3: this.address.address_line3,
          zip_code: this.address.zip_code,
          city: this.address.city,
          country: this.address.country
        },
        contact: {
          firstName: this.contact.firstName,
          lastName: this.contact.lastName,
          tel: this.contact.tel,
          email: this.contact.email
        }
      }
  
    if (this.router.url.startsWith("/service/merchant/edit")) {
      this.merchantService.merchantContactInfo = contactInfoInit;
    } else if (this.router.url.startsWith("/service/supplier/edit")) {
      this.supplierService.supplierContactInfo = contactInfoInit;
    }
    }
  }

  initForm() : void {
    this.contactForm = this.formBuilder.group({
      firstName: new FormControl(!this.isCreateMode ? this.contact.firstName :'', Validators.compose([
        Validators.required,
        Validators.pattern(this.contactNameRegex)
      ])),
      lastName: new FormControl(!this.isCreateMode ? this.contact.lastName :'', Validators.compose([
        Validators.required,
        Validators.pattern(this.contactNameRegex)
      ])),
      line1: new FormControl(!this.isCreateMode ? this.address.address_line1 :'', Validators.required),
      line2: new FormControl(!this.isCreateMode ? this.address.address_line2 :''),
      line3: new FormControl(!this.isCreateMode ? this.address.address_line3 :''),
      city: new FormControl(!this.isCreateMode ? this.address.city :'', Validators.required),
      country: new FormControl(!this.isCreateMode ? this.address.country :'', Validators.required),
      zip: new FormControl(!this.isCreateMode ? this.address.zip_code :'', Validators.compose([
        Validators.required,
        Validators.pattern(this.zipRegex)
      ])),
      phone: new FormControl(!this.isCreateMode ? this.contact.tel :'', Validators.compose([
        Validators.required,
        Validators.pattern(this.phoneRegex)
      ]))

    });

    if (this.router.url.startsWith("/service/merchant/edit") || this.router.url.startsWith("/service/merchant/create")) {
      this.contactForm.addControl('email', new FormControl(!this.isCreateMode ? this.contact.email :'', Validators.compose([Validators.required, Validators.pattern(this.emailRegex)]), [this.merchantEmailValidator()]));
    } else if (this.router.url.startsWith("/service/supplier/edit") || this.router.url.startsWith("/service/supplier/create")) {
      this.contactForm.addControl('email', new FormControl(!this.isCreateMode ? this.contact.email :'', Validators.compose([Validators.required, Validators.pattern(this.emailRegex)]), [this.supplierEmailValidator()]));
    }

    this.contactForm.valueChanges.subscribe(val => {
      const newEntityContactInfo = {
        address: {
          address_line1: val.line1,
          address_line2: val.line2,
          address_line3: val.line3,
          zip_code: val.zip,
          city: val.city,
          country: val.country
        },
        contact: {
          firstName: val.firstName,
          lastName: val.lastName,
          tel: val.phone,
          email: val.email
        }
      }

      if (this.router.url.startsWith("/service/merchant/edit") || this.router.url.startsWith("/service/merchant/create")) {
        this.merchantService.setMerchantContactInfo(newEntityContactInfo);
      } else if (this.router.url.startsWith("/service/supplier/edit") || this.router.url.startsWith("/service/supplier/create")) {
        this.supplierService.setSupplierContactInfo(newEntityContactInfo);
      }
    });
    
    this.contactForm.statusChanges.subscribe(() => this.contactFormIsValidEvent.emit(this.contactForm.valid && this.contactForm.dirty))
  }

  get contactControl() {
    return this.contactForm.controls;
  }

  save() : void {
    if (this.router.url.startsWith("/service/merchant/edit")) {
      this.merchantService.updateMerchant().subscribe(resp => {
        this.notifService.notifySuccess('SAVED');
      });
    } else if (this.router.url.startsWith("/service/supplier/edit")) {
      this.supplierService.updateSupplier().subscribe(resp => {
        this.notifService.notifySuccess('SAVED');
      });
    }
  }
  
  transSearchFn = (term: string, item: any) => {
    term = term.toLocaleLowerCase();
    return this.translateService.instant(item.countryLabel).toLocaleLowerCase().indexOf(term) > -1;
  }
  
  merchantEmailValidator() : AsyncValidatorFn {
    return (control: AbstractControl) : Observable<ValidationErrors> | null => {
      return this.merchantService.isEmailExists(control.value).pipe(
        map(emailExists => emailExists && this.contact.email !== control.value ? {exists:true} : null)
      );
    }
  }

  supplierEmailValidator() : AsyncValidatorFn {
    return (control: AbstractControl) : Observable<ValidationErrors> | null => {
      return this.supplierService.isEmailExists(control.value).pipe(
        map(emailExists => emailExists && this.contact.email !== control.value ? {exists:true} : null)
      );
    }
  }

}
